import { ReactNode } from 'react';
import cn from 'classnames';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IncomingCallsRequest } from '../../../../store/reducers/IncomingCallsSlice';
import { IncomingCallsTablet } from '../../../../models/IncomingCalls/IncomingCalls';
import styles from './style.module.css';
import sharedCellStyles from '../../../../components/shared/tableCellContent.module.css';
import { taskResultLabel } from '../../../../models/enums/TaskResult';
import { incomingJournalSortMap } from '../../../../hooks/shared/journalSortMaps';
import { withServerSorter } from '../../../../hooks/shared/useServerTableSort';
import {
  JournalResultLabelMap,
  JournalScenarioLabelMap,
} from '../../../../hooks/shared/useJournalQueryState';

interface Props {
  callRequest: IncomingCallsRequest;
  scenarioLabelByType: JournalScenarioLabelMap;
  resultLabelByValue?: JournalResultLabelMap;
}

const renderHeader = (title: string) => (
  <div className={sharedCellStyles.headerCenter} title={title}>
    <span className={sharedCellStyles.headerText}>{title}</span>
  </div>
);

const renderHeaderWithTooltip = (title: string, tooltip: ReactNode) => (
  <div className={sharedCellStyles.headerCenter} title={title}>
    <Tooltip title={tooltip} className={sharedCellStyles.headerText}>
      <span className={sharedCellStyles.headerText}>{title}</span>
    </Tooltip>
  </div>
);

export const useIncomingCallsColumns = ({
  callRequest,
  scenarioLabelByType,
  resultLabelByValue = {},
}: Props) => {
  const baseColumns: ColumnsType<IncomingCallsTablet> = [
    {
      title: renderHeader('Сценарий'),
      key: 'type',
      dataIndex: 'type',
      width: 220,
      render: (text: string) => {
        const label = scenarioLabelByType[text] ?? text ?? '';
        return (
          <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
            <span className={sharedCellStyles.textPrimary}>{label}</span>
          </div>
        );
      },
    },
    {
      title: renderHeaderWithTooltip(
        'Дата звонка',
        <>
          <div>Для обработанных: дата последнего звонка</div>
          <div>Для остальных: плановая дата звонка</div>
        </>,
      ),
      key: 'date',
      dataIndex: 'date',
      width: 120,
      render: (text: string) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span
            className={cn(styles.tabletText, sharedCellStyles.textDate)}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </div>
      ),
      sorter: true,
    },
    {
      title: renderHeader('Пациент'),
      dataIndex: 'patientFullname',
      width: 280,
      render: (text: string, record: IncomingCallsTablet) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span className={sharedCellStyles.cellStack}>
            <span className={sharedCellStyles.textPatientName}>{text}</span>
            <span className={sharedCellStyles.textPatientPhone}>
              {record.phone}
            </span>
          </span>
        </div>
      ),
    },
    {
      title: renderHeader('Медицинская организация'),
      dataIndex: 'clinic',
      width: 320,
      render: (text?: string) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span className={sharedCellStyles.textClinicName}>
            {text || 'Нет данных'}
          </span>
        </div>
      ),
    },
    {
      title: renderHeader('Результат'),
      dataIndex: 'result',
      width: 160,
      render: (value?: string) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span
            className={cn(
              sharedCellStyles.textResult,
              value === 'NotReceived'
                ? sharedCellStyles.textResultMuted
                : sharedCellStyles.textResultDefault,
            )}
          >
            {value
              ? (resultLabelByValue[value] ?? taskResultLabel(value))
              : '—'}
          </span>
        </div>
      ),
    },
  ];

  return {
    COLUMNS: withServerSorter(baseColumns, callRequest, incomingJournalSortMap),
    hoveredStatus: null,
  };
};
